/**
 * clipboard.ts - Cut and copy of the current selection to the system clipboard.
 *
 * The selection is serialized at the moment the action runs, from a fresh model read, so nothing
 * here is subscribed to and no consumer re-renders as the selection moves.
 */

import {clearSelection} from 'notebook/tables/store/logic/mutations/clearCellsInRange';
import {type TableMutationParams} from 'notebook/tables/store/logic/mutations/mutationParams';
import {
  computeCopySelectionHtml,
  computeCopySelectionText,
} from 'notebook/tables/store/logic/TableClipboardLogic';
import notifications from 'notifications';
import {checkClipboardWriteAccess, ClipboardItemType} from 'util/clipboard/ClipboardUtil';

/**
 * Writes the current selection to the clipboard as both plain text and an HTML table, which
 * preserves formulas and formatting when pasted back into a Benchling table. Warns with
 * `failureMessage` when the write fails. Returns whether the selection was written.
 */
async function writeSelectionToClipboard(
  params: TableMutationParams,
  failureMessage: string
): Promise<boolean> {
  const allowed = await checkClipboardWriteAccess();
  if (!allowed) {
    return false;
  }
  const {selection, props} = params.getTableModel();
  const copyParams = {
    hasRealSelection: selection.hasRealSelection,
    expandedBox: selection.expandedBox,
    table: props.table,
    computedTable: props.computedTable,
    showFormulas: false,
  };
  const text = computeCopySelectionText(copyParams);
  const html = computeCopySelectionHtml({...copyParams, disableFormulas: props.disableFormulas ?? false});
  try {
    await navigator.clipboard.write([
      // The HTML holds everything in each cell (formulas, links, etc.); the plain text carries only
      // the cells' text.
      new ClipboardItem({
        [ClipboardItemType.TEXT]: new Blob([text], {type: ClipboardItemType.TEXT}),
        [ClipboardItemType.HTML]: new Blob([html], {type: ClipboardItemType.HTML}),
      }),
    ]);
    return true;
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch {
    notifications.warn(failureMessage);
    return false;
  }
}

/** Copies the current selection to the clipboard. */
export async function copySelection(params: TableMutationParams): Promise<void> {
  await writeSelectionToClipboard(params, 'Unable to copy to clipboard.');
}

/** Copies the current selection to the clipboard, then clears it once the copy succeeds. */
export async function cutSelection(params: TableMutationParams): Promise<void> {
  if (await writeSelectionToClipboard(params, 'Unable to cut to clipboard.')) {
    clearSelection(params);
  }
}

/**
 * Whether the current selection may be cut. A selection containing a cell locked on the table
 * itself cannot be cleared, so it cannot be cut.
 */
export function canCutSelection(params: TableMutationParams): boolean {
  return !params.getTableModel().locking.hasLockedCellInSelection;
}
